import { Router, type IRouter, type Request, type Response } from "express";

const router: IRouter = Router();

const SPLAY_API = "https://api.splay.id";
const SPLAY_KEY = "sk_live_afb4c24ea8c44d30a06c67ecc67a8f85a46a0987c8d899bc";

const headers = {
  Authorization: `Bearer ${SPLAY_KEY}`,
  "Content-Type": "application/json",
};

interface CacheEntry {
  data: unknown;
  ts: number;
}

export const splayCache = new Map<string, CacheEntry>();
const DRAMA_TTL = 5 * 60 * 1000;
const ANIME_HOME_TTL = 3 * 60 * 1000;
const ANIME_DETAIL_TTL = 10 * 60 * 1000;
const EPISODE_TTL = 25 * 60 * 1000;

const cache = splayCache;

function getCached(key: string, ttl: number): unknown | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > ttl) { cache.delete(key); return null; }
  return entry.data;
}

export function setCachedExternal(key: string, data: unknown) {
  cache.set(key, { data, ts: Date.now() });
  if (cache.size > 2000) {
    const oldest = [...cache.keys()][0];
    cache.delete(oldest);
  }
}

function setCached(key: string, data: unknown) {
  setCachedExternal(key, data);
}

async function proxyGet(req: Request, res: Response, path: string, ttl = DRAMA_TTL) {
  try {
    const query = new URLSearchParams(req.query as Record<string, string>).toString();
    const url = `${SPLAY_API}${path}${query ? "?" + query : ""}`;
    const cacheKey = url;

    const cached = getCached(cacheKey, ttl);
    if (cached) {
      res.set("X-Cache", "HIT");
      res.json(cached);
      return;
    }

    res.set("X-Cache", "MISS");
    const upstream = await fetch(url, { headers });
    const data = await upstream.json();

    if (upstream.ok) {
      setCached(cacheKey, data);
    }
    res.status(upstream.status).json(data);
  } catch (err) {
    req.log.error({ err }, "Splay proxy error");
    res.status(502).json({ error: "Upstream error" });
  }
}

router.get("/dramas", (req, res) => proxyGet(req, res, "/api/dramas", DRAMA_TTL));
router.get("/dramas/popular", (req, res) => proxyGet(req, res, "/api/dramas/popular", DRAMA_TTL));
router.get("/dramas/trending", (req, res) => proxyGet(req, res, "/api/dramas/trending", DRAMA_TTL));
router.get("/dramas/alphabet", (req, res) => proxyGet(req, res, "/api/dramas/alphabet", DRAMA_TTL));
router.get("/dramas/:id/episodes", (req, res) =>
  proxyGet(req, res, `/api/dramas/${req.params.id}/episodes`, EPISODE_TTL)
);
router.get("/dramas/:id", (req, res) =>
  proxyGet(req, res, `/api/dramas/${req.params.id}`, ANIME_DETAIL_TTL)
);
router.get("/search", (req, res) => proxyGet(req, res, "/api/search", 60 * 1000));
router.get("/providers", (req, res) => proxyGet(req, res, "/api/providers", 60 * 60 * 1000));
router.get("/tags", (req, res) => proxyGet(req, res, "/api/tags", 30 * 60 * 1000));

router.get("/anime/home", (req, res) => proxyGet(req, res, "/api/anime/home", ANIME_HOME_TTL));
router.get("/anime/popular", (req, res) => proxyGet(req, res, "/api/anime/popular", ANIME_HOME_TTL));
router.get("/anime/latest-update", (req, res) => proxyGet(req, res, "/api/anime/latest-update", ANIME_HOME_TTL));
router.get("/anime/season", (req, res) => proxyGet(req, res, "/api/anime/season", DRAMA_TTL));
router.get("/anime/upcoming", (req, res) => proxyGet(req, res, "/api/anime/upcoming", DRAMA_TTL));
router.get("/anime/genres", (req, res) => proxyGet(req, res, "/api/anime/genres", 60 * 60 * 1000));
router.get("/anime/spotlight", (req, res) => proxyGet(req, res, "/api/anime/spotlight", ANIME_HOME_TTL));
router.get("/anime/search", (req, res) => proxyGet(req, res, "/api/anime/search", 60 * 1000));
router.get("/anime/:id/episodes/:ep", (req, res) =>
  proxyGet(req, res, `/api/anime/${req.params.id}/episodes/${req.params.ep}`, EPISODE_TTL)
);
router.get("/anime/:id/episodes", (req, res) =>
  proxyGet(req, res, `/api/anime/${req.params.id}/episodes`, ANIME_DETAIL_TTL)
);
router.get("/anime/:id", (req, res) =>
  proxyGet(req, res, `/api/anime/${req.params.id}`, ANIME_DETAIL_TTL)
);
router.get("/anime", (req, res) => proxyGet(req, res, "/api/anime", ANIME_HOME_TTL));

const MOVIEBOX_TTL = 5 * 60 * 1000;
router.get("/moviebox/popular", (req, res) => proxyGet(req, res, "/api/moviebox/popular", MOVIEBOX_TTL));
router.get("/moviebox/trending", (req, res) => proxyGet(req, res, "/api/moviebox/trending", MOVIEBOX_TTL));
router.get("/moviebox/search", (req, res) => proxyGet(req, res, "/api/moviebox/search", 60 * 1000));
router.get("/moviebox/:id/episodes", (req, res) =>
  proxyGet(req, res, `/api/moviebox/${req.params.id}/episodes`, EPISODE_TTL)
);
router.get("/moviebox/:id", (req, res) =>
  proxyGet(req, res, `/api/moviebox/${req.params.id}`, ANIME_DETAIL_TTL)
);
router.get("/moviebox", (req, res) => proxyGet(req, res, "/api/moviebox", MOVIEBOX_TTL));

const WETV_TTL = 5 * 60 * 1000;
router.get("/wetv/popular", (req, res) => proxyGet(req, res, "/api/wetv/popular", WETV_TTL));
router.get("/wetv/trending", (req, res) => proxyGet(req, res, "/api/wetv/trending", WETV_TTL));
router.get("/wetv/search", (req, res) => proxyGet(req, res, "/api/wetv/search", 60 * 1000));
router.get("/wetv/alphabet", (req, res) => proxyGet(req, res, "/api/wetv/alphabet", WETV_TTL));
router.get("/wetv/:id/episodes", (req, res) =>
  proxyGet(req, res, `/api/wetv/${req.params.id}/episodes`, EPISODE_TTL)
);
router.get("/wetv/:id", (req, res) =>
  proxyGet(req, res, `/api/wetv/${req.params.id}`, ANIME_DETAIL_TTL)
);
router.get("/wetv", (req, res) => proxyGet(req, res, "/api/wetv", WETV_TTL));

const DRAKOR_TTL = 5 * 60 * 1000;
router.get("/drakor/popular", (req, res) => proxyGet(req, res, "/api/drakor/popular", DRAKOR_TTL));
router.get("/drakor/trending", (req, res) => proxyGet(req, res, "/api/drakor/trending", DRAKOR_TTL));
router.get("/drakor/search", (req, res) => proxyGet(req, res, "/api/drakor/search", 60 * 1000));
router.get("/drakor/alphabet", (req, res) => proxyGet(req, res, "/api/drakor/alphabet", DRAKOR_TTL));
router.get("/drakor/:id/episodes", (req, res) =>
  proxyGet(req, res, `/api/drakor/${req.params.id}/episodes`, EPISODE_TTL)
);
router.get("/drakor/:id", (req, res) =>
  proxyGet(req, res, `/api/drakor/${req.params.id}`, ANIME_DETAIL_TTL)
);
router.get("/drakor", (req, res) => proxyGet(req, res, "/api/drakor", DRAKOR_TTL));

const IQIYI_TTL = 5 * 60 * 1000;
router.get("/iqiyi/popular", (req, res) => proxyGet(req, res, "/api/iqiyi/popular", IQIYI_TTL));
router.get("/iqiyi/trending", (req, res) => proxyGet(req, res, "/api/iqiyi/trending", IQIYI_TTL));
router.get("/iqiyi/search", (req, res) => proxyGet(req, res, "/api/iqiyi/search", 60 * 1000));
router.get("/iqiyi/alphabet", (req, res) => proxyGet(req, res, "/api/iqiyi/alphabet", IQIYI_TTL));
router.get("/iqiyi/:id/episodes", (req, res) =>
  proxyGet(req, res, `/api/iqiyi/${req.params.id}/episodes`, EPISODE_TTL)
);
router.get("/iqiyi/:id", (req, res) =>
  proxyGet(req, res, `/api/iqiyi/${req.params.id}`, ANIME_DETAIL_TTL)
);
router.get("/iqiyi", (req, res) => proxyGet(req, res, "/api/iqiyi", IQIYI_TTL));

const BACA_TTL = 5 * 60 * 1000;

const MANGA_PLACEHOLDER_SVG = (page: number) => `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="800" height="1200" viewBox="0 0 800 1200">
  <rect width="800" height="1200" fill="#1a1a1a"/>
  <rect x="20" y="20" width="760" height="1160" fill="#111" rx="4" stroke="#333" stroke-width="1"/>
  <rect x="40" y="40" width="720" height="550" fill="#1e1e1e" rx="2" stroke="#2a2a2a" stroke-width="1"/>
  <rect x="40" y="610" width="350" height="300" fill="#1e1e1e" rx="2" stroke="#2a2a2a" stroke-width="1"/>
  <rect x="410" y="610" width="350" height="300" fill="#1e1e1e" rx="2" stroke="#2a2a2a" stroke-width="1"/>
  <rect x="40" y="930" width="720" height="250" fill="#1e1e1e" rx="2" stroke="#2a2a2a" stroke-width="1"/>
  <text x="400" y="600" font-family="sans-serif" font-size="18" fill="#444" text-anchor="middle">Halaman ${page}</text>
  <text x="400" y="1170" font-family="sans-serif" font-size="13" fill="#333" text-anchor="middle">${page}</text>
</svg>`;

router.get("/baca/image-proxy", async (req, res) => {
  const contentUrl = req.query.content_url as string;
  const page = parseInt((req.query.page as string) || "1", 10);

  if (!contentUrl) {
    res.status(400).json({ error: "content_url required" });
    return;
  }

  const EXTS = ["jpg", "png", "webp", "gif"];
  const [basePath, queryString] = contentUrl.split("?");
  const cleanBase = basePath.replace(/\/$/, "");
  const pageNum = String(page).padStart(3, "0");
  const qs = queryString ? `?${queryString}` : "";

  for (const ext of EXTS) {
    const imageUrl = `${cleanBase}/${pageNum}.${ext}${qs}`;
    try {
      const upstream = await fetch(imageUrl, {
        headers: {
          "User-Agent": "Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1",
          Referer: "https://splay.id/",
          Origin: "https://splay.id",
        },
      });
      if (upstream.ok) {
        const contentType = upstream.headers.get("content-type") || `image/${ext}`;
        res.setHeader("Content-Type", contentType);
        res.setHeader("Cache-Control", "public, max-age=3600");
        const arrayBuffer = await upstream.arrayBuffer();
        res.send(Buffer.from(arrayBuffer));
        return;
      }
    } catch {
    }
  }

  res.setHeader("Content-Type", "image/svg+xml");
  res.setHeader("Cache-Control", "public, max-age=300");
  res.send(MANGA_PLACEHOLDER_SVG(page));
});

router.get("/baca/:category/popular", (req, res) =>
  proxyGet(req, res, `/api/baca/${req.params.category}/popular`, BACA_TTL)
);
router.get("/baca/:category/trending", (req, res) =>
  proxyGet(req, res, `/api/baca/${req.params.category}/trending`, BACA_TTL)
);
router.get("/baca/:category/search", (req, res) =>
  proxyGet(req, res, `/api/baca/${req.params.category}/search`, 60 * 1000)
);
router.get("/baca/:category/alphabet", (req, res) =>
  proxyGet(req, res, `/api/baca/${req.params.category}/alphabet`, BACA_TTL)
);
router.get("/baca/:category/:id/chapters", (req, res) =>
  proxyGet(req, res, `/api/baca/${req.params.category}/${req.params.id}/chapters`, EPISODE_TTL)
);
router.get("/baca/:category/:id", (req, res) =>
  proxyGet(req, res, `/api/baca/${req.params.category}/${req.params.id}`, ANIME_DETAIL_TTL)
);
router.get("/baca/:category", (req, res) =>
  proxyGet(req, res, `/api/baca/${req.params.category}`, BACA_TTL)
);

export default router;
